import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ForumIcon from '@mui/icons-material/Forum';
import default_theme from 'assets/themes/themes';
import styles from './ShareButton.module.css';

const ShareButton = ({ 
  url, 
  title, 
  description,
  fullWidth = true,
  variant = "outlined",
  size = "medium"
}) => {
  const [open, setOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const shareUrl = url || window.location.href;
  const shareTitle = title || document.title;
  const shareDescription = description || '';

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showNotification = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showNotification('Link copied to clipboard!');
      handleClose();
    } catch (err) {
      showNotification('Failed to copy link', 'error');
    }
  };

  // Share to WhatsApp
  const handleWhatsAppShare = () => {
    const text = `${shareTitle}\n${shareDescription}\n${shareUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
    handleClose();
  };

  // Share to Email
  const handleEmailShare = () => {
    const subject = encodeURIComponent(shareTitle);
    const body = encodeURIComponent(
      `${shareDescription}\n\nView this solution here: ${shareUrl}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    handleClose();
  };

  // Share to Mattermost
  const handleMattermostShare = () => {
    // Mattermost doesn't have a direct share URL like WhatsApp
    // This copies the formatted message to clipboard for easy pasting
    const mattermostMessage = `**${shareTitle}**\n${shareDescription}\n[View Solution](${shareUrl})`;
    
    navigator.clipboard.writeText(mattermostMessage).then(() => {
      showNotification('Message copied! Paste it in Mattermost');
      handleClose();
    }).catch(() => {
      showNotification('Failed to copy message', 'error');
    });
  };

  const shareOptions = [
    {
      label: 'Copy link',
      icon: <ContentCopyIcon sx={{ fontSize: 28 }} />,
      onClick: handleCopyLink,
      color: default_theme.palette.other.main,
    },
    {
      label: 'WhatsApp',
      icon: <WhatsAppIcon sx={{ fontSize: 28 }} />,
      onClick: handleWhatsAppShare,
      color: default_theme.palette.other.socialWhatsApp,
    },
    {
      label: 'Email',
      icon: <EmailIcon sx={{ fontSize: 28 }} />,
      onClick: handleEmailShare,
      color: default_theme.palette.other.socialGoogle,
    },
    {
      label: 'Mattermost',
      icon: <ForumIcon sx={{ fontSize: 28 }} />,
      onClick: handleMattermostShare,
      color: default_theme.palette.other.socialLinkedIn,
    },
  ];

  return (
    <>
      <Tooltip title="Share this solution">
        <Button
          fullWidth={fullWidth}
          variant={variant}
          size={size}
          startIcon={<ShareIcon />}
          onClick={handleOpen}
          className={styles.shareButton}
          sx={{
            color: default_theme.palette.other.main,
            borderColor: default_theme.palette.other.main,
            '&:hover': {
              backgroundColor: default_theme.palette.other.tealHover,
              borderColor: default_theme.palette.other.tealDark,
            },
          }}
        >
          Share Solution
        </Button>
      </Tooltip>

      {/* Share Modal */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          className: styles.dialogPaper
        }}
      >
        <DialogTitle className={styles.dialogTitle}>
          <Typography variant="h5" className={styles.dialogTitleText}>
            Share Solution
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            className={styles.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.dialogContent}>
          {/* Solution Title Preview */}
          <Box
            className={styles.previewBox}
            sx={{
              backgroundColor: default_theme.palette.other.backgroundGrey,
            }}
          >
            <Typography
              variant="body2"
              className={styles.previewTitle}
              sx={{
                color: default_theme.palette.font.darkGray,
              }}
            >
              {shareTitle}
            </Typography>
            {shareDescription && (
              <Typography
                variant="caption"
                className={styles.previewDescription}
                sx={{
                  color: 'text.secondary',
                }}
              >
                {shareDescription}
              </Typography>
            )}
          </Box>

          <Box className={styles.shareOptionsGrid}>
            {shareOptions.map((option) => (
              <Box
                key={option.label}
                onClick={option.onClick}
                className={styles.shareOption}
                sx={{
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: option.color,
                  },
                }}
              >
                <Box
                  className={styles.shareOptionIcon}
                  sx={{
                    color: option.color,
                  }}
                >
                  {option.icon}
                </Box>
                <Typography
                  variant="body2"
                  className={styles.shareOptionLabel}
                  sx={{
                    color: 'text.primary',
                  }}
                >
                  {option.label}
                </Typography>
              </Box>
            ))}
          </Box>
          
          <Box
            className={styles.urlDisplay}
            sx={{
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="caption"
              className={styles.urlText}
              sx={{
                color: 'text.secondary',
              }}
            >
              {shareUrl}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ShareButton;